import { gql } from '@apollo/client/core'
import {
  ArcTeamDto,
  ArcTeamGetArgs, ArcTeamGetByIdArgs, ArcTeamMemberStatusCounts, ArcTeamPagedResultDto,
  CreateArcTeamDto, EntityDeletionSummaryDto,
  UpdateArcTeamDto
} from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Replaces `ArcTeamService`.
 *
 * `countMembers` and `countFollowers` are pure arithmetic over a dto the caller already holds — no api
 * call — and `countFollowers` calls `countMembers`, which is why they are plain module functions here
 * rather than bag entries referring to each other through `service`.
 */

const crud = useBaseCrud<ArcTeamDto>('arcTeam')

/**
 * What the team lists show. The counts come from the server and the reader's own membership arrives
 * as `clientUserTeamMember`. These used to fetch every member of every team (and, for authors, every
 * auto-action email) just to count them or find the reader's own row here: 264 KB for a typical
 * reader's three teams [sc-28163].
 */
const arcTeamListItem = gql`
  fragment ArcTeamListItemFragment on ArcTeamDto {
    id,
    name,
    description,
    image,
    defaultDisclaimer,
    applicantCount,
    memberCount,
    clientUserTeamMember {
      id,
      status,
      statusDate,
      reviewerStatus,
      type,
      userId
    },
    isDraft,
    followerCount,
    isActivePublisherTeam,
    userIsFollowing,
    averageRating,
    reviewCount
  }
`

const arcTeamFragments = {
  arcTeamListItem,
  // A single team, which adds the auto actions for the edit form.
  arcTeam: gql`
    fragment ArcTeamFragment on ArcTeamDto {
      ...ArcTeamListItemFragment,
      autoActionEmailAddresses {
        emailAddress,
        type
      }
    }
    ${arcTeamListItem}
  `
}

const get = async (params?: ArcTeamGetArgs): Promise<ArcTeamPagedResultDto> => {
  return useApollo().query({
    variables: {
      ...params
    },
    query: gql`
      query GetArcTeams (
        $genericSearchTerm: String,
        $genericSearch: Boolean,
        $teamMemberId: Int,
        $skip: Int,
        $take: Int,
        $sortBy: String
        $status: Int
        $excludeTeamsUserIsAMemberOf: Int
        $forUserId: Int
      ) {
        arcTeams (
          genericSearchTerm: $genericSearchTerm,
          genericSearch: $genericSearch,
          teamMemberId: $teamMemberId,
          skip: $skip,
          take: $take,
          sortBy: $sortBy
          status: $status
          excludeTeamsUserIsAMemberOf: $excludeTeamsUserIsAMemberOf
          forUserId: $forUserId
        ) {
          totalRows,
          items {
            ...ArcTeamListItemFragment
          }
        }
      }
      ${arcTeamFragments.arcTeamListItem}
    `
  }).then((result: any) => {
    return {
      items: result.items,
      totalRows: result.totalRows
    }
  })
}

/**
 * `args` is everything but the id, which arrives as the first parameter and is merged in below — the
 * same over-strict signature penName's getById had, hidden the same way by
 * `BaseCrudServiceInterface.getById (id?: number, args?: any)`.
 */
const getById = (id: number, args?: Omit<ArcTeamGetByIdArgs, 'id'>): Promise<ArcTeamDto> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id,
      ...args
    },
    query: gql`
      query GetArcTeam (
        $id: Int!,
        $genericSearch: Boolean,
        $getAverageRating: Boolean
      ) {
        arcTeam (
          id: $id,
          genericSearch: $genericSearch,
          getAverageRating: $getAverageRating
        ) {
          ...ArcTeamFragment
        }
      }
      ${arcTeamFragments.arcTeam}
    `
  })
}

const create = (model: CreateArcTeamDto): Promise<ArcTeamDto> => {
  return useApollo().mutate({
    variables: {
      name: model.name,
      description: model.description,
      image: model.image,
      defaultDisclaimer: model.defaultDisclaimer,
      autoApproveEmailAddresses: model.autoApproveEmailAddresses,
      autoVipEmailAddresses: model.autoVipEmailAddresses,
      autoBlockEmailAddresses: model.autoBlockEmailAddresses
    },
    mutation: gql`
      mutation CreateArcTeam (
        $name: String!,
        $description: String,
        $image: String,
        $defaultDisclaimer: Int,
        $autoApproveEmailAddresses: [String!],
        $autoVipEmailAddresses: [String!],
        $autoBlockEmailAddresses: [String!]
      ) {
        createArcTeam (
          name: $name,
          description: $description,
          image: $image,
          defaultDisclaimer: $defaultDisclaimer,
          autoApproveEmailAddresses: $autoApproveEmailAddresses,
          autoVipEmailAddresses: $autoVipEmailAddresses,
          autoBlockEmailAddresses: $autoBlockEmailAddresses
        ) {
          ...ArcTeamFragment
        }
      }
      ${arcTeamFragments.arcTeam}
    `
  })
}

const update = (model: UpdateArcTeamDto): Promise<ArcTeamDto> => {
  return useApollo().mutate({
    variables: {
      id: model.id,
      name: model.name,
      description: model.description,
      image: model.image,
      defaultDisclaimer: model.defaultDisclaimer,
      autoApproveEmailAddresses: model.autoApproveEmailAddresses,
      autoVipEmailAddresses: model.autoVipEmailAddresses,
      autoBlockEmailAddresses: model.autoBlockEmailAddresses
    },
    mutation: gql`
      mutation UpdateArcTeam (
        $id: Int!,
        $name: String,
        $description: String,
        $image: String,
        $defaultDisclaimer: Int,
        $autoApproveEmailAddresses: [String!],
        $autoVipEmailAddresses: [String!],
        $autoBlockEmailAddresses: [String!]
      ) {
        updateArcTeam (
          id: $id,
          name: $name,
          description: $description,
          image: $image,
          defaultDisclaimer: $defaultDisclaimer,
          autoApproveEmailAddresses: $autoApproveEmailAddresses,
          autoVipEmailAddresses: $autoVipEmailAddresses,
          autoBlockEmailAddresses: $autoBlockEmailAddresses
        ) {
          ...ArcTeamFragment
        }
      }
      ${arcTeamFragments.arcTeam}
    `
  })
}

const checkDelete = (id: number): Promise<EntityDeletionSummaryDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query CheckDeleteArcTeam ($id: Int!) {
        checkDeleteArcTeam (id: $id) {
          count,
          dtoTypeName,
          ids
        }
      }
    `
  })
}

const deleteArcTeam = (id?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation ConfirmDeleteArcTeam (
        $id: Int!
      ) {
        confirmDeleteArcTeam(id: $id) {
          id
        }
      }
    `
  })
}

const countFollowers = (arcTeam: ArcTeamDto | undefined): number => {
  return countMembers(arcTeam) + (arcTeam?.followerCount || 0)
}

/** Active members, as counted by the server. */
const countMembers = (arcTeam: ArcTeamDto | undefined): number => {
  return arcTeam?.memberCount ?? 0
}

const follow = (teamId?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id: teamId
    },
    mutation: gql`
      mutation FollowTeam (
        $id: Int!
      ) {
        followTeam (id: $id) {
          id
        }
      }
    `
  })
}

const unfollow = (teamId?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id: teamId
    },
    mutation: gql`
      mutation UnfollowTeam (
        $id: Int!
      ) {
        unfollowTeam (id: $id) {
          id
        }
      }
    `
  })
}

const getTeamMemberStatusCounts = async (args: ArcTeamGetByIdArgs): Promise<ArcTeamMemberStatusCounts> => {
  return useApollo().query({
    variables: {
      id: args.id
    },
    query: gql`
      query GetTeamMemberStatusCounts ($id: Int!) {
        getTeamMemberStatusCounts (id: $id) {
          awaiting,
          active,
          invited,
          rejected,
          blocked
        }
      }
    `
  })
}

/**
 * `globalLoader` is chained by the team list, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get,
  getById,
  create,
  update,
  checkDelete,
  delete: deleteArcTeam,
  countFollowers,
  countMembers,
  follow,
  unfollow,
  getTeamMemberStatusCounts,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function arcTeamService () {
  return service
}
